import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2, XCircle, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";

const KEY_LABELS = {
  TWILIO_ACCOUNT_SID: "Account SID",
  TWILIO_API_KEY_SID: "API Key SID",
  TWILIO_API_KEY_SECRET: "API Key Secret",
  TWILIO_MESSAGING_SERVICE_SID: "Messaging Service SID (or From number)",
  TWILIO_FROM_NUMBER: "Toll-free From number (or Messaging Service)",
};

const Row = ({ ok, label, testid }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testid}>
    {ok ? <CheckCircle2 size={14} className="text-green-500" /> : <XCircle size={14} className="text-race-muted" />}
    <span className={ok ? "text-race-txt" : "text-race-sub"}>{label}</span>
  </div>
);

const ST = {
  delivered: "text-green-500 border-green-500/50 bg-green-500/10", read: "text-green-500 border-green-500/50 bg-green-500/10",
  failed: "text-red-500 border-red-500/50 bg-red-500/10", undelivered: "text-red-500 border-red-500/50 bg-red-500/10",
};
export const SmsStatusBadge = ({ status, testid }) => (
  <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm shrink-0 ${ST[status] || "text-sky-500 border-sky-500/50 bg-sky-500/10"}`} data-testid={testid}>{status}</span>
);
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function RecentTexts({ tick }) {
  const [rows, setRows] = useState([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const load = () => api.get(`/admin/sms-reminders/log${failed ? "?failed=true" : ""}`).then((r) => setRows(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [failed, tick]);
  return (
    <div className="bg-race-surface border border-race-line" data-testid="sms-log">
      <div className="flex items-center justify-between px-5 py-3 border-b border-race-line">
        <div className="overline text-[10px] text-race-sub">Recent texts</div>
        <label className="flex items-center gap-2 text-xs font-mono text-race-sub cursor-pointer">
          <input type="checkbox" checked={failed} onChange={(e) => setFailed(e.target.checked)} data-testid="sms-log-failed-only" /> Failures only
        </label>
      </div>
      {rows.length === 0 ? <div className="px-5 py-6 text-sm text-race-sub" data-testid="sms-log-empty">{failed ? "No failed texts." : "No texts sent yet."}</div> : (
        <div className="divide-y divide-race-line">
          {rows.map((r) => (
            <div key={r.id} className="px-5 py-2.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm" data-testid={`sms-log-row-${r.id}`}>
              <SmsStatusBadge status={r.status} testid={`sms-log-status-${r.id}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate"><span className="text-race-txt">{r.racer || r.email || "Racer"}</span> <span className="text-race-muted">· {r.to}</span> <span className="text-race-sub">· {r.race || r.type}</span></div>
                {r.error && <div className="text-xs text-red-500" data-testid={`sms-log-error-${r.id}`}>{r.error}{r.code ? ` (${r.code})` : ""}</div>}
              </div>
              <div className="font-mono text-[11px] text-race-muted shrink-0">{when(r.at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SmsReminders() {
  const [tick, setTick] = useState(0);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/sms-reminders").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/sms-reminders", { enabled: !s.admin_enabled }); setS(data); toast.success(data.admin_enabled ? "SMS reminders switched on" : "SMS reminders switched off"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/sms-reminders/test"); toast.success(`Test text sent to ${data.to}. Delivery result appears below within a minute.`); load(); setTick((t) => t + 1); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!s) return <div className="text-race-sub text-sm">Loading…</div>;
  const k = s.keys;
  const sender = k.TWILIO_MESSAGING_SERVICE_SID || k.TWILIO_FROM_NUMBER;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="sms-reminders">
      <div className="bg-race-surface border border-race-line p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="text-race-red mt-0.5" size={20} />
          <div>
            <div className="font-heading text-xl tracking-tight">SMS Race Reminders</div>
            <p className="text-race-sub text-xs mt-1 max-w-md">Texts each opted-in racer at their reminder time (3/5/10 min), alongside push + email. Only racers who ticked the SMS consent box on their profile; STOP replies are honoured automatically.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border rounded-sm ${s.active ? "text-green-500 border-green-500/50 bg-green-500/10" : "text-amber-500 border-amber-500/50 bg-amber-500/10"}`} data-testid="sms-status">
            {s.active ? "Sending" : "Not sending"}
          </span>
          <button onClick={toggle} disabled={busy} data-testid="sms-toggle"
            className={`px-3 py-2 rounded-sm text-xs font-mono border transition-colors ${s.admin_enabled ? "border-race-line text-race-sub hover:text-race-txt" : "border-race-red text-race-red hover:bg-race-red/10"}`}>
            {s.admin_enabled ? "Switch off" : "Switch on"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-race-surface border border-race-line p-5 space-y-2" data-testid="sms-checklist">
          <div className="overline text-[10px] text-race-sub mb-1">Setup checklist</div>
          <Row ok={k.TWILIO_ACCOUNT_SID} label={KEY_LABELS.TWILIO_ACCOUNT_SID} testid="sms-key-account" />
          <Row ok={k.TWILIO_API_KEY_SID && k.TWILIO_API_KEY_SECRET} label="API Key SID + Secret" testid="sms-key-apikey" />
          <Row ok={sender} label="Sender: Messaging Service SID or approved toll-free number" testid="sms-key-sender" />
          <Row ok={s.env_switch} label="SMS_REMINDERS_ENABLED = true (deployment secret)" testid="sms-key-switch" />
          <Row ok={s.admin_enabled} label="Admin switch on" testid="sms-key-admin" />
        </div>
        <div className="bg-race-surface border border-race-line p-5 space-y-3">
          <div className="overline text-[10px] text-race-sub">Last 7 days</div>
          <div className="flex gap-6 font-mono">
            <div><div className="text-2xl text-race-txt" data-testid="sms-sent">{s.sent_7d}</div><div className="text-[10px] text-race-sub uppercase">sent</div></div>
            <div><div className="text-2xl text-race-txt" data-testid="sms-failed">{s.failed_7d}</div><div className="text-[10px] text-race-sub uppercase">failed</div></div>
            <div><div className="text-2xl text-race-txt" data-testid="sms-optin">{s.opted_in_racers}</div><div className="text-[10px] text-race-sub uppercase">opted-in racers</div></div>
          </div>
          <button onClick={test} disabled={busy || !s.sender_configured} data-testid="sms-test"
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-race-line text-xs font-mono hover:border-race-red/60 disabled:opacity-40">
            <Send size={13} /> Send test text to my phone
          </button>
        </div>
      </div>
      <RecentTexts tick={tick} />
    </div>
  );
}
